import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from "@angular/core";
import { DropDownModel } from "../../../domain/model/common/dropDownModel";

import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { ExternalUser } from "../../../domain/model/account/externalUser";
import { RentalProperty } from "../../../domain/model/properties/rentalProperty";
import { CommonServices } from "../../../domain/service/commonService";
import { GooglePlacesService } from "../../../domain/service/GooglePlaceService";
import { PropertyService } from "../../../domain/service/propertyService";
import { AuthenticationService } from "../../../security/auth/authentication.service";
import { AccountType } from "../../../enums/accountType";
import { DatePipe } from "@angular/common";

@Component({
  selector: "app-properties-detail-info",
  templateUrl: "./properties-detail-info.component.html",
  styleUrl: "./properties-detail-info.component.css",
})
export class PropertiesDetailInfoComponent implements OnInit, OnChanges {
  states: DropDownModel[] = [];
  @Input() rentalPropertyModel!: RentalProperty;
  @Output() formChanged: EventEmitter<boolean> = new EventEmitter<boolean>();
  public rentalProperty!: FormGroup;
  public hasChange: boolean = false;
  externalUser!: ExternalUser | null;
  currentState = "";
  constructor(private datePipe: DatePipe,
    private googlePlacesService: GooglePlacesService,
    private propertyService: PropertyService,
    private commonServices: CommonServices,
    private _formBuilder: FormBuilder,
    private fb: FormBuilder,
    private readonly auth: AuthenticationService
  ) {
    this.externalUser = this.auth.getCurrentUser2();
  }

  /** Office NAME for the read-only field (the property stores the office number). */
  appraiserOfficeName = '';
  private resolveOfficeName(): void {
    const stored = String(this.rentalProperty.get('appraiserOfficeId')?.value ?? '');
    if (!stored) { this.appraiserOfficeName = ''; return; }
    this.commonServices.getAppraiserOffices().subscribe(list => {
      const o = list.find(x => String(x.officeNumber) === stored || String(x.id) === stored);
      this.appraiserOfficeName = o ? `${o.name}${o.officeNumber ? ' (' + o.officeNumber + ')' : ''}` : stored;
    });
  }

  ngOnInit(): void {
    this.commonServices.getAllState(233).subscribe((data) => {
      this.states = data;
      this.currentState = this.states.find((x) => x.id == this.rentalPropertyModel?.stateId)?.name ?? "";
    });

    this.rentalProperty.get("isUsingTaxRepresentative")?.setValue(null);
  }
  ngOnChanges(changes: SimpleChanges): void {
    this.buildForm(this.rentalPropertyModel);
  }

  public setFormData(info: RentalProperty | undefined): void {
    this.rentalProperty = this.fb.group({
      propertyStreetAddress: new FormControl(info?.rentalPropertyStreetAddress),
      aptUnitSuite: [info?.aptUnitSute ?? "", Validators.required],
      city: [info?.city ?? "", Validators.required],
      state: [info?.stateId ?? "", Validators.required],
      zipCode: [
        info?.zipCode ?? "",
        Validators.required,
        Validators.maxLength(400),

        Validators.pattern(/(^\d{5}$)|(^\d{5}-\d{4}$)/),
      ],
      filingFrequency: [info?.frequency ?? "", Validators.required],
      businessName: [info?.businessName ?? ""],
      webUrl: [info?.webUrl ?? "", Validators.pattern(/^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([\/\w .-]*)*\/?$/)],
      usePlatformToRent: [info?.useDigitalPlatform ?? false],
      platFormLink: new FormControl({ value: info?.digitalPlatform, disabled: true }),
      rentalPropertyBusiness: [info?.businessName ?? ""],
      jurisdiction: new FormControl(""),
      propertyNickName: new FormControl(""),
    });
  }
  private buildForm(info: RentalProperty | undefined): void {
    this.currentState = this.states.find((x) => x.id == info?.stateId)?.name ?? "";
  
    const date = this.datePipe.transform(info?.startedRenting ,"MM-dd-yyyy") ;
  
    const selectedDate = new Date(date??'');
    this.rentalProperty = this.fb.group({
      propertyStreetAddress: [info?.rentalPropertyStreetAddress ?? ""],
      aptUnitSuite: [info?.aptUnitSute ?? ""],
      city: [info?.city ?? "", Validators.required],
      state: [info?.stateId ?? "", Validators.required],
      zipCode: [info?.zipCode ?? "", Validators.required],
      filingFrequency: [info?.frequency, Validators.required],

      businessName: [info?.businessName ?? ""],
      webUrl: [info?.webUrl ?? "", Validators.pattern(/^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([\/\w .-]*)*\/?$/)],
      usePlatformToRent: [info?.useDigitalPlatform ?? false],
      platFormLink: [info?.digitalPlatform ?? false],
      rentalPropertyBusiness: [info?.businessName ?? ""],
      startedRenting: [selectedDate, Validators.required],
      propertyType: [info?.propertyTypeDescription ?? ""],
      jurisdiction: [info?.jurisdiction ?? ""],
      appraiserOfficeId: [info?.appraiserOfficeId ?? ""],
      propertyNickName: [info?.nickName ?? ""],
    });
    this.resolveOfficeName();
    if (info?.useDigitalPlatform === true){
      this.rentalProperty.get("platFormLink")?.disable();
      this.rentalProperty.get("platFormLink")?.setValue("");
    }
    this.rentalProperty.get("usePlatformToRent")?.valueChanges.subscribe((checked) => {
      const platFormLinkControl = this.rentalProperty.get("platFormLink");
      if (checked) { 
        this.rentalProperty.get("platFormLink")?.disable();
        this.rentalProperty.get("platFormLink")?.setValue("");
      } else {
        platFormLinkControl?.addValidators([Validators.required]);
        platFormLinkControl?.enable();
      }
    });
    if (this.externalUser?.accountType != AccountType.INDIVIDUAL_OWNER.valueOf()){
    
      this.rentalProperty.get("filingFrequency")?.disable();

    }
  
    this.rentalProperty.valueChanges.subscribe((newValues) => {
      this.hasChange = true;
      this.formChanged.emit(true);
    });
  }

  hasErrors(): boolean {
    const controls = this.rentalProperty.controls;
    for (const controlName in controls) {
      if (controls.hasOwnProperty(controlName)) {
        const control = controls[controlName];
        const statusPrevious = control.enabled;
        control.enable();
        if (control.invalid) {
          return false;
        } else if (!statusPrevious) {
          control.disable();
        }
      }
    }
    return true;
  }
  public save() {
    let model: RentalProperty = {
      id: this.rentalPropertyModel.id,
      accountID: this.rentalPropertyModel.accountID,
      rentalPropertyStreetAddress: this.rentalProperty.get("propertyStreetAddress")?.value ?? "",
      aptUnitSute: this.rentalProperty.get("aptUnitSuite")?.value ?? "",
      city: this.rentalProperty.get("city")?.value ?? "",
      stateId: this.rentalProperty.get("state")?.value ?? 0,
      zipCode: this.rentalProperty.get("zipCode")?.value ?? "",
      startedRenting: this.rentalProperty?.get("startedRenting")?.value ?? "",
      frequency: this.rentalProperty?.get("filingFrequency")?.value ?? "",
      businessName: this.rentalProperty.get("businessName")?.value ?? "",
      webUrl: this.rentalProperty.get("webUrl")?.value ?? "",
      useDigitalPlatform: this.rentalProperty?.get("usePlatformToRent")?.value ?? false,
      digitalPlatform: this.rentalProperty.get("platFormLink")?.value ?? "",
      jurisdiction: this.rentalProperty.get("jurisdiction")?.value ?? "",
      appraiserOfficeId: this.rentalProperty.get("appraiserOfficeId")?.value ?? "",
      propertyType: 0,
      isUsingTaxRepresentative: true,
      correspondenceToOwner: true,
      correspondenceToReportingRepresentative: true,
      reportingOwner: true,
      reportingPropertyManagementCompany: true,
      status: this.rentalPropertyModel.status,
      createdDate: "",
      propertyTypeDescription: "",
      statusDescription: "",
      nickName: this.rentalProperty.get("propertyNickName")?.value ?? "",
    };

    this.propertyService.updateProperty(model, this.rentalPropertyModel.accountID.toString()).subscribe({
      next: (response) => {},
      error: (error) => {},
      complete: () => {},
    });
  }
}
